import React, { useEffect, useRef, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
} from "lucide-react";
import { galleryImages } from "../data/mockData";

const PREVIEW_COUNT = 3;

const Gallery = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (activeIndex === null) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setActiveIndex(null);
      if (e.key === "ArrowRight")
        setActiveIndex((i) => (i + 1) % galleryImages.length);
      if (e.key === "ArrowLeft")
        setActiveIndex(
          (i) => (i - 1 + galleryImages.length) % galleryImages.length
        );
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [activeIndex]);

  const active = activeIndex !== null ? galleryImages[activeIndex] : null;

  // No images yet — hide the section gracefully until photos are added.
  if (!galleryImages.length) return null;

  const hasMore = galleryImages.length > PREVIEW_COUNT;
  const visibleImages =
    showAll || !hasMore ? galleryImages : galleryImages.slice(0, PREVIEW_COUNT);

  return (
    <section
      id="gallery"
      ref={sectionRef}
      className="py-28 bg-white"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-brand-mint-dark text-xs font-bold uppercase tracking-[0.25em] mb-4">
            Our Work
          </p>
          <h2 className="font-heading text-3xl lg:text-4xl text-gray-900 mb-4">
            Recent Installs & Jobs
          </h2>
          <p className="text-gray-500 text-[15px] max-w-xl mx-auto leading-relaxed">
            A snapshot of recent residential, commercial and industrial AC
            projects across Yorkshire.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
          {visibleImages.map((img, i) => (
            <button
              key={img.src}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`group relative overflow-hidden rounded-xl bg-gray-100 aspect-square focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold transition-all duration-350 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: `${(i % PREVIEW_COUNT) * 40}ms` }}
              data-testid={`gallery-thumb-${i}`}
            >
              <img
                loading="lazy"
                decoding="async"
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-350 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <span className="absolute bottom-3 left-3 right-3 text-white text-xs sm:text-sm font-semibold tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-left">
                {img.caption}
              </span>
            </button>
          ))}
        </div>

        {/* Show more / less toggle */}
        {hasMore && (
          <div className="flex justify-center mt-10">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-charcoal text-white hover:bg-brand-charcoal-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
              data-testid="gallery-toggle-button"
              aria-expanded={showAll}
              aria-label={showAll ? "Show fewer photos" : "Show all photos"}
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-gold text-brand-charcoal transition-transform duration-300 group-hover:scale-110">
                {showAll ? (
                  <Minus className="w-4 h-4" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
              </span>
              {showAll ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-brand-charcoal-dark/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveIndex(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex(null);
            }}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close gallery"
            data-testid="gallery-close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex(
                (i) => (i - 1 + galleryImages.length) % galleryImages.length
              );
            }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Previous"
            data-testid="gallery-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex((i) => (i + 1) % galleryImages.length);
            }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Next"
            data-testid="gallery-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div
            className="relative max-w-5xl w-full max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.src}
              alt={active.alt}
              className="max-h-[78vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white/85 text-sm mt-4 font-medium tracking-wide">
              {active.caption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
